import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Company } from "@ligament/shared";
import { companiesApi } from "../api/companies";
import { ApiError } from "../api/client";
import { queryKeys } from "../lib/queryKeys";

type PracticeSelectionSource = "manual" | "route_sync" | "bootstrap";
type PracticeSelectionOptions = { source?: PracticeSelectionSource };

interface PracticeContextValue {
  companies: Company[];
  selectedPracticeId: string | null;
  selectedPractice: Company | null;
  selectionSource: PracticeSelectionSource;
  loading: boolean;
  error: Error | null;
  setSelectedPracticeId: (companyId: string, options?: PracticeSelectionOptions) => void;
  reloadPractices: () => Promise<void>;
  createPractice: (data: {
    name: string;
    description?: string | null;
    budgetMonthlyCents?: number;
  }) => Promise<Company>;
}

const STORAGE_KEY = "ligament.selectedCompanyId";

const PracticeContext = createContext<PracticeContextValue | null>(null);

export function PracticeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [selectionSource, setSelectionSource] = useState<PracticeSelectionSource>("bootstrap");
  const [selectedPracticeId, setSelectedPracticeIdState] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));

  const { data: companies = [], isLoading, error } = useQuery({
    queryKey: queryKeys.companies.all,
    queryFn: async () => {
      try {
        return await companiesApi.list();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          return [];
        }
        throw err;
      }
    },
    retry: false,
  });
  const sidebarPractices = useMemo(
    () => companies.filter((company) => company.status !== "archived"),
    [companies],
  );

  // Auto-select first company when list loads
  useEffect(() => {
    if (companies.length === 0) return;

    const selectablePractices = sidebarPractices.length > 0 ? sidebarPractices : companies;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && selectablePractices.some((c) => c.id === stored)) return;
    if (selectedPracticeId && selectablePractices.some((c) => c.id === selectedPracticeId)) return;

    const next = selectablePractices[0]!.id;
    setSelectedPracticeIdState(next);
    setSelectionSource("bootstrap");
    localStorage.setItem(STORAGE_KEY, next);
  }, [companies, selectedPracticeId, sidebarPractices]);

  const setSelectedPracticeId = useCallback((companyId: string, options?: PracticeSelectionOptions) => {
    setSelectedPracticeIdState(companyId);
    setSelectionSource(options?.source ?? "manual");
    localStorage.setItem(STORAGE_KEY, companyId);
  }, []);

  const reloadPractices = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
  }, [queryClient]);

  const createMutation = useMutation({
    mutationFn: (data: { name: string; description?: string | null; budgetMonthlyCents?: number }) =>
      companiesApi.create(data),
    onSuccess: (company) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
      setSelectedPracticeId(company.id);
    },
  });

  const createPractice = useCallback(
    async (data: { name: string; description?: string | null; budgetMonthlyCents?: number }) => {
      return createMutation.mutateAsync(data);
    },
    [createMutation],
  );

  const selectedPractice = useMemo(
    () => companies.find((company) => company.id === selectedPracticeId) ?? null,
    [companies, selectedPracticeId],
  );

  const value = useMemo(
    () => ({
      companies,
      selectedPracticeId,
      selectedPractice,
      selectionSource,
      loading: isLoading,
      error: error as Error | null,
      setSelectedPracticeId,
      reloadPractices,
      createPractice,
    }),
    [
      companies,
      selectedPracticeId,
      selectedPractice,
      selectionSource,
      isLoading,
      error,
      setSelectedPracticeId,
      reloadPractices,
      createPractice,
    ],
  );

  return <PracticeContext.Provider value={value}>{children}</PracticeContext.Provider>;
}

export function usePractice() {
  const ctx = useContext(PracticeContext);
  if (!ctx) {
    throw new Error("usePractice must be used within PracticeProvider");
  }
  return ctx;
}
